import {
  createApiClient,
  downloadCsv,
  type Board,
  type Distribution,
  type Invitation,
  type StayStatus,
} from '@rooming/web-core';

// Typed calls of the passenger/guardian app (API_SPECIFICATION.md §2, §8, §9). One client instance per app.

export const api = createApiClient();

export type SessionStage = 'MFA_PENDING' | 'CREDENTIAL_CHANGE_PENDING' | 'ACTIVE';

export interface Me {
  id: string;
  kind: 'STAFF' | 'PASSENGER' | 'APODERADO';
  username: string | null;
  displayName: string;
}

export interface MyStay {
  id: string;
  status: StayStatus;
  checkInAt: string;
  checkOutAt: string;
  selectionOpensAt: string | null;
  selectionClosesAt: string | null;
  version: number;
  hotel: { name: string; city: string; timezone: string } | null;
}

export interface AssignmentResult {
  slotId: string | null;
  version: number | null;
  stayVersion: number;
  changed: boolean;
  /** Undefined sex asked for a women's or men's bed: nothing was assigned, an administrator decides (HTTP 202). */
  pendingRequest?: { id: string; slotId: string; roomLabel: string };
}

/** The passenger's own request for a bed that needs an administrator's approval. */
export interface PlacementRequest {
  id: string;
  stayId: string;
  slotId: string;
  roomLabel: string;
  position: number;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED' | 'SUPERSEDED';
  createdAt: string;
  resolvedAt: string | null;
}

export interface SwapRequest {
  id: string;
  /** OUTGOING: I asked; INCOMING: someone asks me. */
  direction: 'OUTGOING' | 'INCOMING';
  status: 'REQUESTED' | 'EXECUTED' | 'REJECTED' | 'CANCELLED' | 'EXPIRED' | 'INVALIDATED';
  myRoom: string;
  otherRoom: string;
  /** Who the other passenger is (DR-371): the two can talk before answering. */
  otherName: string;
  createdAt: string;
  expiresAt: string;
  resolvedAt: string | null;
}

/** One passenger of the group and where they are (the FIRMANTE's view, prompt10 §3). */
export interface RosterEntry {
  memberId: string;
  firstNames: string;
  lastNames: string;
  sex: string;
  /** A student or a companion (a parent who travels). */
  role: 'PASSENGER' | 'COMPANION';
  room: string | null;
  physicalRoom: string | null;
  /** Only in the signatory's explicit, audited read of documents (prompt11 §5). */
  document?: { type: string; number: string };
  /** PENDING = has not chosen their own password yet: the signatory shares the group link with them (DR-408). */
  accountState?: 'ACTIVATED' | 'PENDING';
}

export const passengerApi = {
  session: () => api.get<{ stage: SessionStage; csrfToken: string }>('/auth/session'),
  login: (username: string, password: string) =>
    api.send<{ nextStep: 'CREDENTIAL_CHANGE' | 'NONE'; csrfToken: string }>('POST', '/auth/passenger-sessions', {
      username,
      password,
    }),
  changeInitialPassword: (newPassword: string) =>
    api.send<{ csrfToken: string }>('POST', '/auth/credential-change', { newPassword }),
  /** The signatory's own group link and progress (DR-405). */
  activationInvitation: () => api.get<Invitation>('/me/activation-invitation'),
  /** The group link, before asking the person for anything: is it usable, and for which group? */
  previewActivation: (token: string) =>
    api.send<{ groupName: string; closesAt: string }>('POST', '/activation/preview', { token }),
  /** Document + password + email through the group link: the account becomes active and the person is signed in. */
  activateAccount: (input: { token: string; document: string; password: string; email: string }) =>
    api.send<{ username: string; csrfToken: string }>('POST', '/activation/accounts', input),
  logout: () => api.send<undefined>('DELETE', '/auth/sessions/current'),
  me: () => api.get<Me>('/me'),
  myStays: () => api.get<{ items: MyStay[] }>('/me/stays'),
  board: (stayId: string) => api.get<Board>(`/stays/${stayId}/board`),
  myAssignment: (stayId: string) =>
    api.get<{ slotId: string | null; version: number | null }>(`/stays/${stayId}/my-assignment`),
  choose: (
    stayId: string,
    slotId: string,
    replaceExisting: boolean,
    precondition: { ifMatch?: string; ifNoneMatch?: string },
    acceptedTermsVersion?: number,
  ) =>
    api.send<AssignmentResult>(
      'PUT',
      `/stays/${stayId}/my-assignment`,
      { slotId, replaceExisting, ...(acceptedTermsVersion === undefined ? {} : { acceptedTermsVersion }) },
      precondition,
    ),
  myPlacementRequest: (stayId: string) =>
    api.get<{ item: PlacementRequest | null }>(`/stays/${stayId}/my-placement-request`),
  cancelPlacementRequest: (stayId: string) =>
    api.send<{ cancelled: boolean }>('DELETE', `/stays/${stayId}/my-placement-request`),
  distribution: (stayId: string) => api.get<Distribution>(`/stays/${stayId}/distribution`),
  /** The designated signing guardian confirms the exact version they are looking at. */
  confirmDistribution: (stayId: string, version: number) =>
    api.send<{ stayVersion: number; confirmedAt: string }>(
      'POST',
      `/stays/${stayId}/distribution-confirmations`,
      undefined,
      { ifMatch: `"v${String(version)}"` },
    ),
  /** The official rooming list (CSV, opened in Excel as UTF-16): the group's signatory downloads it (2026-10-28). */
  downloadRoomingList: (stayId: string) => downloadCsv(api, `/stays/${stayId}/rooming-list.csv`, 'rooming-list.csv'),
  roster: (stayId: string, documents = false) =>
    api.get<{ items: RosterEntry[] }>(`/stays/${stayId}/passengers${documents ? '?documents=true' : ''}`),
  swaps: (stayId: string) => api.get<{ enabled: boolean; items: SwapRequest[] }>(`/stays/${stayId}/swap-requests`),
  askSwap: (stayId: string, targetSlotId: string) =>
    api.send<SwapRequest>('POST', `/stays/${stayId}/swap-requests`, { targetSlotId }),
  answerSwap: (id: string, answer: 'acceptance' | 'rejection' | 'cancellation') =>
    api.send<unknown>('POST', `/swap-requests/${id}/${answer}`, answer === 'cancellation' ? {} : undefined),
  release: (stayId: string, ifMatch: string | undefined) =>
    api.send<AssignmentResult>(
      'DELETE',
      `/stays/${stayId}/my-assignment`,
      undefined,
      ifMatch === undefined ? {} : { ifMatch },
    ),
};
